import { useEffect, useState } from 'react';

/**
 * A typewriter placeholder: a fixed prefix that never animates, plus
 * continuations that type and delete themselves after it. Each continuation
 * carries its own leading space, so the prefix reads as one sentence with
 * whichever tail is showing (e.g. "Ask Strive" + " to build a report that...").
 */
export interface TypewriterPlaceholder {
  /** The fixed lead-in, always visible. */
  prefix: string;
  /** Continuations cycled through after the prefix, leading space included. */
  tails: string[];
}

// Timings from the approved homepage artifact: slow enough to read each
// character, brisk enough to grasp in one pass.
const TYPE_MS = 58; // per character, typing
const DELETE_MS = 28; // per character, deleting — a little faster
const HOLD_FULL_MS = 1400; // pause on the completed phrase
const HOLD_EMPTY_MS = 550; // pause on the bare prefix before the next one

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function readReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(REDUCED_MOTION_QUERY).matches
    : false;
}

/**
 * Types the tails of a {@link TypewriterPlaceholder} character by character
 * after its fixed prefix, deleting each back to the bare prefix before moving
 * to the next, and returns the string currently showing.
 *
 * When the user prefers reduced motion (or `enabled` is false) the animation
 * stops and the prefix + last tail is shown statically — the last tail is the
 * catch-all phrasing (e.g. " anything"), so the static form still reads whole.
 */
export function useTypewriterPlaceholder(
  { prefix, tails }: TypewriterPlaceholder,
  { enabled = true }: { enabled?: boolean } = {},
): string {
  const [text, setText] = useState(prefix);

  // Track the live reduced-motion preference so toggling it mid-session
  // starts/stops the animation rather than freezing the mount-time value.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(readReducedMotion);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setPrefersReducedMotion(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  // The tails array is compared by content so a re-created (but identical)
  // array literal doesn't restart the animation every render.
  const tailsKey = JSON.stringify(tails);

  useEffect(() => {
    const tailList = JSON.parse(tailsKey) as string[];
    if (!enabled) {
      setText(prefix);
      return;
    }
    if (prefersReducedMotion || tailList.length === 0) {
      setText(prefix + (tailList[tailList.length - 1] ?? ''));
      return;
    }

    let tailIndex = 0; // which continuation
    let charCount = 0; // characters of it currently shown
    let deleting = false;
    let timer: number;

    const step = () => {
      const tail = tailList[tailIndex];
      if (!deleting) {
        charCount += 1;
        setText(prefix + tail.slice(0, charCount));
        if (charCount >= tail.length) {
          deleting = true;
          timer = window.setTimeout(step, HOLD_FULL_MS);
        } else {
          timer = window.setTimeout(step, TYPE_MS);
        }
        return;
      }
      charCount -= 1;
      setText(prefix + tail.slice(0, Math.max(charCount, 0)));
      if (charCount <= 0) {
        // Bare prefix again — hold, then move to the next continuation.
        deleting = false;
        charCount = 0;
        tailIndex = (tailIndex + 1) % tailList.length;
        timer = window.setTimeout(step, HOLD_EMPTY_MS);
      } else {
        timer = window.setTimeout(step, DELETE_MS);
      }
    };

    setText(prefix);
    timer = window.setTimeout(step, HOLD_EMPTY_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, prefersReducedMotion, prefix, tailsKey]);

  return text;
}
